import { Ellipsis } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { useLayer } from '../../lib/layers';
import { Icon } from './Icon';

export interface MenuItem {
  /** Stable key; defaults to the item's index. */
  readonly id?: string;
  readonly label: ReactNode;
  readonly onSelect: () => void;
  /** `danger` renders the destructive colour (design: "Archive" in #A8381F). */
  readonly tone?: 'default' | 'danger';
  readonly disabled?: boolean;
  /** Right-hand key hint (11px, fg-3). */
  readonly kbd?: ReactNode;
  /** Draws the 1px separator above this item. */
  readonly separatorBefore?: boolean;
}

export interface MenuProps {
  readonly items: readonly MenuItem[];
  /** Accessible name of the trigger; default «Действия со строкой». */
  readonly label?: string;
  /** Trigger glyph; default lucide `ellipsis`. */
  readonly icon?: LucideIcon;
  readonly triggerClassName?: string;
  /** Visible trigger content instead of the icon (then `triggerClassName` styles the whole button). */
  readonly triggerContent?: ReactNode;
  readonly onOpenChange?: (open: boolean) => void;
}

/** Gap kept between the menu and the viewport edge when it would overflow. */
const VIEWPORT_MARGIN = 8;

/*
 * z-index: the design's row menu is z 20 inside `main`. Portaled to <body>, the menu must also
 * sit above a drawer (40) or dialog (50) that opened it, and below toasts (60): z 58.
 */

/**
 * Row action menu (design MC:293-300): a 28px "…" trigger and a popover menu (min-width 200,
 * padding 4, border-1, radius 10, shadow-md; items 30px, 13px, radius 6, hover neutral-100).
 * The popover is portaled with fixed positioning so the grid's `overflow: clip` never cuts it.
 * ↑/↓/Home/End move between items, Enter/Space run one, Esc or Tab closes and returns focus to
 * the trigger; a click outside, a scroll or a resize closes it. Clicks never reach the row.
 */
export function Menu({
  items,
  label: labelProp,
  icon = Ellipsis,
  triggerClassName,
  triggerContent,
  onOpenChange,
}: MenuProps) {
  const label = labelProp ?? 'Действия со строкой';
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const setMenuOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const onTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuOpen(!open);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerContent ? undefined : label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={onTriggerClick}
        className={
          triggerContent
            ? triggerClassName
            : cn(
                'grid size-7 flex-none place-items-center rounded-1 border-0 bg-transparent text-fg-2 hover:bg-neutral-100',
                triggerClassName,
              )
        }
      >
        {triggerContent ?? <Icon icon={icon} />}
      </button>
      {open && (
        <MenuPopover
          id={menuId}
          label={label}
          items={items}
          triggerRef={triggerRef}
          onClose={() => {
            setMenuOpen(false);
            triggerRef.current?.focus({ preventScroll: true });
          }}
        />
      )}
    </>
  );
}

interface MenuPopoverProps {
  readonly id: string;
  readonly label: string;
  readonly items: readonly MenuItem[];
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  readonly onClose: () => void;
}

function MenuPopover({ id, label, items, triggerRef, onClose }: MenuPopoverProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const position = useAnchoredPosition(triggerRef, menuRef);
  useLayer({ kind: 'menu', panelRef: menuRef, onClose });
  useDismissOnOutside(menuRef, triggerRef, onClose);

  const select = (item: MenuItem) => {
    if (item.disabled) return;
    onClose();
    item.onSelect();
  };

  return createPortal(
    <div
      ref={menuRef}
      id={id}
      role="menu"
      aria-label={label}
      tabIndex={-1}
      onKeyDown={(event) => moveFocus(event, menuRef.current)}
      onClick={(event) => event.stopPropagation()}
      className="fixed z-[58] flex min-w-[200px] flex-col rounded-2 border border-border-1 bg-bg-3 p-1 text-[13px] text-fg-1 shadow-md outline-none"
      style={position}
    >
      {items.map((item, index) => (
        <Fragment key={item.id ?? index}>
          {item.separatorBefore && <div role="separator" className="my-1 h-px bg-border-1" />}
          <button
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={() => select(item)}
            className={cn(
              'flex h-[30px] items-center justify-between gap-3 whitespace-nowrap rounded-1 border-0 bg-transparent px-2.5 text-left text-[13px]',
              'hover:bg-neutral-100 focus-visible:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-40',
              item.tone === 'danger' ? 'text-danger-fg' : 'text-fg-1',
            )}
          >
            {item.label}
            <span className="text-[11px] text-fg-3">{item.kbd}</span>
          </button>
        </Fragment>
      ))}
    </div>,
    document.body,
  );
}

const MOVE_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);

/** Roving focus between enabled menu items (wraps at both ends). */
function moveFocus(event: KeyboardEvent<HTMLDivElement>, menu: HTMLDivElement | null): void {
  if (!menu || !MOVE_KEYS.has(event.key)) return;
  event.preventDefault();
  const enabled = Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])'));
  if (enabled.length === 0) return;
  const current = enabled.indexOf(document.activeElement as HTMLElement);
  const last = enabled.length - 1;
  const next =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? last
        : event.key === 'ArrowDown'
          ? (current + 1) % enabled.length
          : (current - 1 + enabled.length) % enabled.length;
  enabled[next]?.focus();
}

/**
 * Places the menu under the trigger with its right edges aligned (design: `top:calc(100% - 4px);
 * right:4px` inside the 36px action cell), flipping above when it would leave the viewport.
 */
function useAnchoredPosition(
  triggerRef: RefObject<HTMLElement | null>,
  menuRef: RefObject<HTMLElement | null>,
): CSSProperties {
  const [position, setPosition] = useState<CSSProperties>({ top: 0, left: 0, visibility: 'hidden' });
  useLayoutEffect(() => {
    const trigger = triggerRef.current?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    if (!trigger || !menu) return;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const fitsBelow = trigger.bottom + menu.height <= viewportHeight - VIEWPORT_MARGIN;
    const top = fitsBelow || trigger.top < menu.height ? trigger.bottom : trigger.top - menu.height;
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, trigger.right - menu.width),
      viewportWidth - menu.width - VIEWPORT_MARGIN,
    );
    setPosition({ top, left: Math.max(VIEWPORT_MARGIN, left) });
  }, [triggerRef, menuRef]);
  return position;
}

/** Closes on a pointer press outside the menu and its trigger, and on scroll or resize. */
function useDismissOnOutside(
  menuRef: RefObject<HTMLElement | null>,
  triggerRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    const isInside = (target: EventTarget | null) =>
      target instanceof Node &&
      (!!menuRef.current?.contains(target) || !!triggerRef.current?.contains(target));
    const onPointerDown = (event: Event) => {
      if (!isInside(event.target)) onCloseRef.current();
    };
    const onScroll = (event: Event) => {
      if (!(event.target instanceof Node && menuRef.current?.contains(event.target))) onCloseRef.current();
    };
    const onResize = () => onCloseRef.current();
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [menuRef, triggerRef]);
}
